import { fetchAllListings, getGlobalState, getMyNfts, getMyETHBalance, getMyProceeds, getMyProviderAddress, getOwners, getPendingOwners, isApprovedMyAddress, isEvmChainInitialized, isMarketOpened, getAllowedBatchSize, getDefaultBatchSize, getWhitelistBatchSize, getScheduledPriceChange, getPriceChangeTimestamp, getPriceTimelock, getMarketFee, getMaxRoyaltyBps, getAccruedMintFees, getAccruedMarketFees, getMarketNftContract, isMintingPaused, weiToEther, } from '../../chains/evm/api';
import { DEFAULT_BATCH_SIZE, WHITELIST_BATCH_SIZE } from '../constants';
export function isEvmChainReady(chain) {
    return isEvmChainInitialized(chain);
}
export async function updateEVMGlobalState(chain, deps, getChainStateFn, patchChainStateFn) {
    const [baseGlobal, isOpened, owners, pendingOwners, defaultRaw, whitelistRaw, scheduledRaw, unlockRaw, priceTimelockRaw, marketFeeRaw, maxRoyaltyRaw, mintingPaused, accruedMintFeesRaw, accruedMarketFeesRaw, marketNftContractRaw,] = await Promise.all([
        getGlobalState(chain).catch(() => null),
        isMarketOpened(chain).catch(() => false),
        getOwners(chain).catch(() => ({ nft: null, market: null })),
        getPendingOwners(chain).catch(() => ({ nft: null, market: null })),
        getDefaultBatchSize(chain).catch(() => null),
        getWhitelistBatchSize(chain).catch(() => null),
        getScheduledPriceChange(chain).catch(() => null),
        getPriceChangeTimestamp(chain).catch(() => null),
        getPriceTimelock(chain).catch(() => null),
        getMarketFee(chain).catch(() => null),
        getMaxRoyaltyBps(chain).catch(() => null),
        isMintingPaused(chain).catch(() => false),
        getAccruedMintFees(chain).catch(() => null),
        getAccruedMarketFees(chain).catch(() => null),
        getMarketNftContract(chain).catch(() => null),
    ]);
    const defaultBatchSize = defaultRaw != null && Number.isFinite(Number(defaultRaw)) && Number(defaultRaw) > 0
        ? Number(defaultRaw)
        : DEFAULT_BATCH_SIZE;
    const whitelistBatchSize = whitelistRaw != null && Number.isFinite(Number(whitelistRaw)) && Number(whitelistRaw) > 0
        ? Number(whitelistRaw)
        : WHITELIST_BATCH_SIZE;
    const scheduledWei = scheduledRaw != null ? BigInt(scheduledRaw.toString()) : BigInt(0);
    const unlockAt = unlockRaw != null && Number.isFinite(Number(unlockRaw)) ? Number(unlockRaw) : 0;
    const gl = baseGlobal == null
        ? null
        : {
            ...baseGlobal,
            scheduledMintPriceWei: scheduledWei.toString(),
            scheduledMintPrice: scheduledWei > BigInt(0) ? weiToEther(scheduledWei) : 0,
            priceChangeUnlockAt: unlockAt,
            priceTimelockSeconds: priceTimelockRaw != null && Number.isFinite(Number(priceTimelockRaw))
                ? Number(priceTimelockRaw)
                : 24 * 60 * 60,
            marketFeeBps: marketFeeRaw != null && Number.isFinite(Number(marketFeeRaw))
                ? Number(marketFeeRaw)
                : null,
            maxRoyaltyBps: maxRoyaltyRaw != null && Number.isFinite(Number(maxRoyaltyRaw))
                ? Number(maxRoyaltyRaw)
                : null,
            isMintingPaused: Boolean(mintingPaused),
            accruedMintFeesWei: accruedMintFeesRaw != null ? accruedMintFeesRaw.toString() : '0',
            accruedMintFees: accruedMintFeesRaw != null ? weiToEther(accruedMintFeesRaw) : 0,
            accruedMarketFeesWei: accruedMarketFeesRaw != null ? accruedMarketFeesRaw.toString() : '0',
            accruedMarketFees: accruedMarketFeesRaw != null ? weiToEther(accruedMarketFeesRaw) : 0,
        };
    deps.setState((prev) => {
        const chainState = getChainStateFn(prev, chain);
        return patchChainStateFn(prev, chain, {
            global: gl,
            nftOwner: owners?.nft,
            marketOwner: owners?.market,
            nftPendingOwner: pendingOwners?.nft,
            marketPendingOwner: pendingOwners?.market,
            marketNftContract: marketNftContractRaw?.toString?.() ?? marketNftContractRaw ?? null,
            isStable: chainState.weiBalance != null && gl != null,
            isMarketOpened: isOpened,
            defaultBatchSize,
            whitelistBatchSize,
        });
    });
}
export async function updateEVMUserState(chain, deps, getChainStateFn, patchChainStateFn) {
    const weiBalance = await getMyETHBalance(chain).catch(() => undefined);
    const isApproved = await isApprovedMyAddress(chain).catch(() => []);
    const walletAddress = await getMyProviderAddress(chain);
    const proceeds = await getMyProceeds(chain);
    const allowedRaw = walletAddress != null
        ? await getAllowedBatchSize(chain, walletAddress).catch(() => null)
        : null;
    const allowedBatch = allowedRaw != null ? Number(allowedRaw) : DEFAULT_BATCH_SIZE;
    deps.setBalances((prev) => ({
        ...prev,
        [chain]: weiBalance,
    }));
    deps.setState((prev) => {
        const chainState = getChainStateFn(prev, chain);
        return patchChainStateFn(prev, chain, {
            balance: weiToEther(weiBalance ?? 0).toFixed(3),
            isApprovedMarket: isApproved,
            isStable: weiBalance != null && chainState.global != null,
            weiBalance,
            walletAddress,
            weiProceeds: proceeds,
            proceeds: weiToEther(proceeds).toFixed(3),
            maxMintCount: allowedBatch,
            maxBuyCount: allowedBatch,
            maxDelistCount: allowedBatch,
            maxListCount: allowedBatch,
            maxPriceUpdateCount: allowedBatch,
        });
    });
}
export async function fetchEvmListedNfts(chain, allListedNfts) {
    return fetchAllListings(allListedNfts, chain);
}
export async function fetchEvmMyNfts(chain, myNfts) {
    return getMyNfts(myNfts, chain);
}
